import usePageVisibilityChange from '@src/hooks/usePageVisibilityChange';
import { useVideoCreatorStore } from '@src/stores-v2';
import { observer } from 'mobx-react-lite';
import Modal from './common/Modal';
import CopyContentModal from './stage/CopyContentModal';
import { useState } from 'react';
import { Video, VideoItem, VideoVersion } from '@src/types.ts/story';
import { CurrentLoadedVideo } from '@src/stores/VideoCreatorStore';
import { ConfirmModal } from './common/ConfirmModal';
import { CopyIcon } from './common/icons';
import CircleExclammationIcon from '@src/svgs/CircleExclammationIcon';
import styled from 'styled-components';

const NewVideoVersionListener = observer(() => {
  const videoCreator = useVideoCreatorStore();

  const [newVideoVersionModal, setNewVersionModal] = useState<{
    type: 'save_as' | 'confirm';
    video: {
      id: Video['id'];
      title: Video['title'];
      lastActionJson: Video['lastActionJson'];
    };
  } | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleNewVideoVersion = (
    currentVideo: CurrentLoadedVideo,
    newVersion: VideoVersion,
  ) => {
    setNewVersionModal({
      type: 'confirm',
      video: {
        id: currentVideo.id,
        title: currentVideo.title,
        lastActionJson: newVersion.lastActionJson,
      },
    });
  };

  const duplicateOutdatedCurrentVideo = async (): Promise<VideoItem> => {
    videoCreator.currentVideo!.punchList =
      videoCreator.punchListManager.punchListItems!;

    await videoCreator.prepareCurrentVideoForSave(!!videoCreator.renderer);

    let videoData: { id: string; slug: string; hash: string };
    videoData =
      await videoCreator.datoClientStore.videoRepository.saveOrUpdateVideo({
        ...videoCreator.currentVideo,
        transcriptionText:
          videoCreator.rootStore.transcriptionStore.fullTranscriptionText,
        transcriptionSnapshot: {
          elements:
            videoCreator.rootStore.transcriptionStore.getFinalTranscriptionElements(),
        },
        subtitles: videoCreator.subtitlesProcessor.getSubtitleLines(),
        id: undefined, // create a new video
      });
    const copy = await videoCreator.datoClientStore.videoRepository.findVideo(
      videoData.id,
    );
    if (!copy) {
      throw new Error('Failed to copy outdated current video');
    }
    return copy;
  };

  const handleSave = async (storyName: string) => {
    if (!newVideoVersionModal) {
      return;
    }
    const { video } = newVideoVersionModal;
    if (video.id) {
      setIsSaving(true);
      try {
        await videoCreator.copyVideo(
          // cannot just pass videoId because it will copy the latest version instead of the current one
          await duplicateOutdatedCurrentVideo(),
          storyName,
          'creator-studio',
        );
      } catch (error) {
        console.error('Error saving outdated video copy:', error);
      } finally {
        setIsSaving(false);
      }
    } else {
      console.error(
        'Error saving outdated video copy: incomplete video object',
        video,
      );
    }
    setNewVersionModal(null);
  };

  const handleConfirm = () => {
    setNewVersionModal((prev) => ({
      ...prev!,
      type: 'save_as',
    }));
  };

  const closeModal = () => {
    setNewVersionModal(null);
  };

  usePageVisibilityChange({
    active: [
      () => {
        videoCreator.refreshManager.handleCurrentVideoVersion(
          handleNewVideoVersion,
        );
      },
    ],
  });

  if (newVideoVersionModal?.type === 'confirm') {
    return (
      <ConfirmModal
        closeModal={closeModal}
        onConfirm={handleConfirm}
        onCancel={closeModal}
        confirmText={
          <>
            <CopyIcon />
            Save As
          </>
        }
        cancelText="Close"
        headingText={
          <>
            <CircleExclammationIcon strokeColor="#ef5da8" />
            <HeadingText>
              {`WARNING ${
                newVideoVersionModal?.video?.lastActionJson?.editor || 'Someone'
              } recently saved.`}
              <br />
              You may be overwriting their changes.
              <br />
              Refresh your page to view their changes
              <br />
              or Save a new copy
              {}
            </HeadingText>
          </>
        }
        accentColor="#17c964"
      />
    );
  }

  if (newVideoVersionModal?.type === 'save_as') {
    return (
      <Modal isOpen closeModal={closeModal}>
        <CopyContentModal
          name={newVideoVersionModal.video?.title}
          tag="Story"
          type={newVideoVersionModal.type}
          onCancel={closeModal}
          onSave={handleSave}
          modalOverrides={{
            wrapperStyles: isSaving
              ? {
                  'pointer-events': 'none',
                }
              : undefined,
            saveButtonContent: isSaving ? (
              <>
                <CopyIcon /> Saving...
              </>
            ) : undefined,
          }}
        />
      </Modal>
    );
  }

  return null;
});

const HeadingText = styled.div`
  color: #f3e9d7;
  margin-bottom: -8px;
`;

export default NewVideoVersionListener;
